import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

/**
 * Opens a new round on a running search (§23.3).
 *
 * Takes a name and nothing else. The endpoint also accepts an area ring, which is how a control room
 * narrows or moves the search between rounds — but drawing a polygon needs the map, and the map waits
 * on the self-hosted tile packs (§14). Omitting the ring makes the server keep the mission's own area,
 * which is the sensible default for "search the same ground again with fresh sectors".
 *
 * Stated rather than hidden: this dialog cannot yet re-shape a search, only re-divide it.
 */
export function StartRoundDialog({
  missionTitle,
  open,
  onOpenChange,
  onConfirm,
  busy,
  serverError,
}: {
  missionTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (title: string) => void;
  busy: boolean;
  /** Shown inside the dialog — see CloseMissionDialog for why it cannot live in the row. */
  serverError?: string | null;
}) {
  const { t } = useLocale();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = title.trim();

    if (!trimmed) {
      setError(t.missions.roundTitleRequired);
      return;
    }

    onConfirm(trimmed);
    setTitle('');
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setTitle('');
          setError(null);
        }

        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t.missions.closeAria}>
        <DialogHeader>
          <DialogTitle>{t.missions.startRoundDialogTitle}</DialogTitle>
          <DialogDescription>{t.missions.startRoundDialogLede}</DialogDescription>
        </DialogHeader>

        <p className="font-semibold">{missionTitle}</p>

        <Field label={t.missions.roundTitleLabel} htmlFor="round-title">
          <input
            id="round-title"
            autoFocus
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError(null);
            }}
            placeholder={t.missions.roundTitlePlaceholder}
            className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary"
          />
        </Field>

        {error ?? serverError ? (
          <p role="alert" className="text-sm text-danger">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.missions.cancel}
          </Button>
          <Button disabled={busy} onClick={submit}>
            {t.missions.confirmStartRound}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
